import { CreateDonationArgs, Location, User, campaigns } from "@ecologi/shared";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import useMedia from "react-use/lib/useMedia";
import { ForestWrapper, Wrapper } from "./OmniForestComponents";
import { AlbertModal } from "./components/AlbertModal";
import { DonateModal, donationTypeMap } from "./components/DonateModal";
import { ForestTitleBox } from "./components/ForestTitleBox";
import { GrassTuftsBackground } from "./components/GrassTuftsBackground";

export type OmniForestProps = {
  children: React.ReactNode;
  initialDonationTab?: typeof donationTypeMap.default;
  isDonateButtonVisible?: boolean;
  isForestHidden?: boolean;
  isOwnProfile: boolean;
  isWaitingForPaymentProcessor: boolean;
  location: Location;
  // Logged out / no payment method donation
  onRedirectToCheckout: (payload: CreateDonationArgs) => Promise<void>;
  // Loggeed in / payment method donation
  onCreateDonationCharge: (payload: CreateDonationArgs) => Promise<void>;
  onCreatePaymentLink: (payload: any) => Promise<void>;
  onDeactivatePaymentLink: (payload: any) => Promise<void>;
  onEditTitle: (payload: any) => Promise<any>;
  profile: User;
  referringService: string;
  resetProfileParamsState: () => void;
  user: User | null;
};

export const OmniForest = ({
  children,
  initialDonationTab,
  isDonateButtonVisible,
  isForestHidden,
  isOwnProfile,
  isWaitingForPaymentProcessor,
  location,
  onCreateDonationCharge,
  onCreatePaymentLink,
  onDeactivatePaymentLink,
  onEditTitle,
  onRedirectToCheckout,
  profile,
  referringService,
  resetProfileParamsState,
  user,
}: OmniForestProps) => {
  const router = useRouter();
  const mobileUp = useMedia("(min-width: 480px)", true);
  const { albertsuccess, session_id } = router.query;

  // We need to retain these to use for the customisations in the donate modal
  const isIntrepid = profile.username === campaigns.intrepid.username;
  const isLapland = profile.username === campaigns.lapland.username;
  const isForestOfLove = profile.username === campaigns.forestOfLove.username;

  const [isDonateModalOpen, setDonateModalStateRaw] = useState(false);
  const [isAlbertModalOpen, setAlbertModalState] = useState(false);
  const [isAlbertConfirmationModalOpen, setalbertConfirmationModal] =
    useState<boolean>(false);

  const setDonateModalState = (state: boolean) => {
    // reset the internal state from profile-hooks, this allows
    // for multiple donations to all work as expected
    resetProfileParamsState();
    setDonateModalStateRaw(state);
  };

  // Open the donation popup on page load if needed
  useEffect(() => {
    if (
      typeof initialDonationTab === "number" &&
      profile.isTreeDonationsEnabled
    ) {
      setDonateModalState(true);
    }
  }, [initialDonationTab, profile]);

  useEffect(() => {
    albertsuccess === "true" ? setalbertConfirmationModal(true) : null;

    if (albertsuccess === "true" && session_id) {
      const sessionId = session_id;
      const userId = profile.userId;
      onDeactivatePaymentLink({ sessionId, userId });

      router.replace(`/${profile.username}`, undefined, {
        shallow: true,
      });
    }
  }, [session_id]);

  return (
    <div>
      <div id="forest">
        <ForestWrapper>
          <Wrapper
            data-testid="profile-forest-section"
            isForestHidden={isForestHidden}
          >
            {!isForestHidden && (
              <>
                <ForestTitleBox
                  isDonateButtonVisible={isDonateButtonVisible}
                  onEditTitle={onEditTitle}
                  user={user}
                  {...{
                    profile,
                    isOwnProfile,
                    setDonateModalState,
                    setAlbertModalState,
                  }}
                />

                {children}
                {mobileUp && <GrassTuftsBackground />}
              </>
            )}
          </Wrapper>
        </ForestWrapper>
      </div>

      {isDonateModalOpen && (
        <DonateModal
          close={() => {
            // Make sure there isn't any params in the route
            router.replace(`/${profile.username}`, undefined, {
              shallow: true,
            });
            setDonateModalState(false);
          }}
          initialDonationTab={initialDonationTab || donationTypeMap.default}
          campaign={
            isIntrepid
              ? campaigns.intrepid.id
              : isLapland
              ? campaigns.lapland.id
              : isForestOfLove
              ? campaigns.forestOfLove.id
              : null
          }
          isOpen={isDonateModalOpen}
          isOwnProfile={isOwnProfile}
          profile={profile}
          referringService={referringService}
          createDonationCharge={async (charge: any) => {
            await onCreateDonationCharge(charge);
            setDonateModalState(false);
          }}
          redirectToCheckout={onRedirectToCheckout}
          user={user}
          location={location}
          isWaitingForPaymentProcessor={isWaitingForPaymentProcessor}
        />
      )}

      {(isAlbertModalOpen || isAlbertConfirmationModalOpen) && (
        <AlbertModal
          close={() => {
            // Make sure there isn't any params in the route
            router.replace(`/${profile.username}`, undefined, {
              shallow: true,
            });
            setAlbertModalState(false);
            setalbertConfirmationModal(false);
          }}
          setAlbertModalState={setAlbertModalState}
          setalbertConfirmationModal={setalbertConfirmationModal}
          isAlbertModalOpen={isAlbertModalOpen}
          isAlbertConfirmationModalOpen={isAlbertConfirmationModalOpen}
          profile={profile}
          createPaymentLink={async (data: any) => {
            return onCreatePaymentLink(data);
          }}
        />
      )}
    </div>
  );
};
